import React, { useState } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
    FormControl, FormLabel, Input, Button, Stack, useToast, InputGroup, InputRightElement,
    InputLeftElement, Icon, FormErrorMessage
} from '@chakra-ui/react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { changePassword } from '../../Services/auth';
import { useLang } from "../../Hooks";

const ChangePasswordModal = ({ isOpen, onClose }) => {
    const toast = useToast();
    const { texts, lang } = useLang(); 
    const isRTL = lang === 'ar';
    
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showOld, setShowOld] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (newPassword !== confirmPassword) {
            setError(texts.modals?.passwordsNotMatch || "New passwords do not match.");
            return;
        }
        
        if (newPassword.length < 6) {
            setError(texts.modals?.passwordMinLength || "New password must be at least 6 characters long.");
            return;
        }

        setIsLoading(true);
        const result = await changePassword(oldPassword, newPassword);
        setIsLoading(false);

        if (result.success) {
            toast({
                title: 'Success',
                description: texts.modals?.changePasswordSuccess || "Password changed successfully",
                status: 'success',
                duration: 5000,
                isClosable: true,
                position: isRTL ? 'top-left' : 'top',
            });
            onClose();
        } else {
            toast({
                title: 'Error',
                description: texts.modals?.changePasswordError || result.error,
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: isRTL ? 'top-left' : 'top',
            });
        }
    };

    const handleClose = () => {
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setError('');
        onClose();
    };

    // دالة مساعدة لتحديد موضع الأيقونة بناءً على اللغة
    const EyeIcon = ({ show, setShow }) => {
        const IconComponent = show ? FaEyeSlash : FaEye;
        
        if (isRTL) {
            return (
                <InputLeftElement>
                    <Icon as={IconComponent} cursor="pointer" onClick={() => setShow(!show)} />
                </InputLeftElement>
            );
        }
        
        return (
            <InputRightElement>
                <Icon as={IconComponent} cursor="pointer" onClick={() => setShow(!show)} />
            </InputRightElement>
        );
    };

    // تخصيص ModalCloseButton بناءً على اللغة
    const CustomModalCloseButton = () => {
        return (
            <ModalCloseButton 
                position="absolute"
                left={isRTL ? 4 : 'auto'}
                right={isRTL ? 'auto' : 4}
                top={4}
            />
        );
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} isCentered>
            <ModalOverlay />
            <ModalContent as="form" onSubmit={handleSubmit} dir={isRTL ? 'rtl' : 'ltr'}>
                <ModalHeader 
                    paddingInlineEnd={isRTL ? 12 : 6}
                    paddingInlineStart={isRTL ? 6 : 12}
                >
                    {texts.header?.changeYourPassword || "Change Your Password"}
                </ModalHeader>
                <CustomModalCloseButton />
                <ModalBody>
                    <Stack spacing={4}>
                        <FormControl isRequired isInvalid={!!error}>
                            <FormLabel>{texts.header?.oldPassword || "Old Password"}</FormLabel>
                            <InputGroup>
                                <Input
                                    type={showOld ? 'text' : 'password'}
                                    value={oldPassword}
                                    onChange={(e) => setOldPassword(e.target.value)}
                                />
                                <EyeIcon show={showOld} setShow={setShowOld} />
                            </InputGroup>
                        </FormControl>

                        <FormControl isRequired isInvalid={!!error}>
                            <FormLabel>{texts.header?.newPassword || "New Password"}</FormLabel>
                            <InputGroup>
                                <Input
                                    type={showNew ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                />
                                <EyeIcon show={showNew} setShow={setShowNew} />
                            </InputGroup>
                        </FormControl>

                        <FormControl isRequired isInvalid={!!error}>
                            <FormLabel>{texts.header?.confirmNewPassword || "Confirm New Password"}</FormLabel>
                            <InputGroup>
                                <Input
                                    type={showConfirm ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                />
                                <EyeIcon show={showConfirm} setShow={setShowConfirm} />
                            </InputGroup>
                            {error && <FormErrorMessage>{error}</FormErrorMessage>}
                        </FormControl>
                    </Stack>
                </ModalBody>
                <ModalFooter>
                    <Button variant='ghost' onClick={handleClose}>
                        {texts.header?.cancel || "Cancel"}
                    </Button>
                    <Button colorScheme='blue' type="submit" isLoading={isLoading} mr={isRTL ? 3 : 0} ml={isRTL ? 0 : 3}>
                        {texts.header?.saveChanges || "Save Changes"}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ChangePasswordModal;